import React from 'react'
import { useAuth } from './AuthProvider'

export function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  // Allow E2E/dev bypass: set localStorage 'pt.auth.bypass' = '1' or use ?e2e=1
  const bypass = (() => {
    try {
      const ls = typeof localStorage !== 'undefined' ? localStorage.getItem('pt.auth.bypass') === '1' : false
      const qs = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('e2e') === '1' : false
      return ls || qs
    } catch {
      return false
    }
  })()

  if (loading) {
    return (
      <div style={{display:'grid', placeItems:'center', minHeight:'100vh'}}>
        <div>Loading…</div>
      </div>
    )
  }

  if (!user && !bypass) return <AuthScreens />

  return <>{children}</>
}

function AuthScreens() {
  const [mode, setMode] = React.useState<'signin'|'signup'>('signin')
  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div style={{display:'grid', placeItems:'center', marginBottom:10}}>
          <img src={new URL('../../PushTrack.comwhiteLogotransparent.png', import.meta.url).toString()} alt="PushTrack" style={{height:56}} />
        </div>
        {mode==='signin' ? <SignInCard onSwitch={() => setMode('signup')} /> : <SignUpCard onSwitch={() => setMode('signin')} />}
      </div>
      {/* Small centered brand mark below */}
      <div style={{display:'grid', placeItems:'center', marginTop:14}}>
        <img src={new URL('../../PushTrack.comwhiteLogotransparent.png', import.meta.url).toString()} alt="PushTrack" style={{height:120, opacity:0.95, filter:'drop-shadow(0 8px 28px rgba(0,0,0,0.45))'}} />
      </div>
    </div>
  )
}

function SignInCard({ onSwitch }: { onSwitch: () => void }) {
  const { signInWithGoogle, signInWithEmail } = useAuth()
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  return (
    <div>
      <p className="auth-subtitle">Log in om door te gaan</p>
      <div style={{display:'grid', gap:8}}>
        <input className="input" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} />
        <input className="input" placeholder="Wachtwoord" type="password" value={password} onChange={e=>setPassword(e.target.value)} />
        {error && <div style={{color:'#ff6b6b', fontSize:12}}>{error}</div>}
  <button onClick={async ()=>{ const res = await signInWithEmail(email, password); if (res.error) setError(res.error)}} className="btn btn-primary btn-block">Log in</button>
  <button onClick={signInWithGoogle} className="btn btn-ghost btn-block">Log in met Google</button>
      </div>
      <div style={{marginTop:10, fontSize:12}}>Nog geen account? <a onClick={onSwitch} style={{cursor:'pointer'}}>Registreren</a></div>
    </div>
  )
}

function SignUpCard({ onSwitch }: { onSwitch: () => void }) {
  const { signUpWithEmail } = useAuth()
  const [form, setForm] = React.useState({
    first_name:'', last_name:'', nickname:'', country_code:'', region:'', postal_code:'', email:'', password:'', age:'', gender:'N'
  })
  const [error, setError] = React.useState<string | null>(null)
  const [info, setInfo] = React.useState<string | null>(null)
  const update = (k: string, v: string) => setForm(s => ({...s, [k]: v}))
  const country = React.useMemo(()=> COUNTRY_OPTIONS.find(c=>c.code===form.country_code) || null,[form.country_code])
  const regions = country?.regions ?? []
  return (
    <div>
      <p className="auth-subtitle">Maak een account aan</p>
      <div style={{display:'grid', gap:8}}>
        <div className="auth-grid-2">
          <input className="input" placeholder="Voornaam" value={form.first_name} onChange={e=>update('first_name', e.target.value)} />
          <input className="input" placeholder="Achternaam" value={form.last_name} onChange={e=>update('last_name', e.target.value)} />
        </div>
        <input className="input" placeholder="Nicknaam (optioneel)" value={form.nickname} onChange={e=>update('nickname', e.target.value)} />
        <div className="auth-grid-3">
          <select className="input" value={form.country_code} onChange={e=>{ update('country_code', e.target.value); update('region','') }}>
            <option value="">Kies land…</option>
            {COUNTRY_OPTIONS.map(c=> (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
          <select className="input" value={form.region} onChange={e=>update('region', e.target.value)} disabled={!form.country_code}>
            <option value="">{form.country_code ? 'Kies provincie/regio…' : 'Kies eerst land'}</option>
            {regions.map(r=> (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <input className="input" placeholder="Postcode (optioneel)" value={form.postal_code} onChange={e=>update('postal_code', e.target.value)} />
        </div>
        <div className="auth-grid-2">
          <input className="input" placeholder="Leeftijd" value={form.age} onChange={e=>update('age', e.target.value)} />
          <select className="input" value={form.gender} onChange={e=>update('gender', e.target.value)}>
            <option value="M">M</option>
            <option value="F">V</option>
            <option value="N">Niet delen</option>
          </select>
        </div>
        <input className="input" placeholder="Emailadres" value={form.email} onChange={e=>update('email', e.target.value)} />
        <input className="input" placeholder="Wachtwoord" type="password" value={form.password} onChange={e=>update('password', e.target.value)} />
        {error && <div style={{color:'#ff6b6b', fontSize:12}}>{error}</div>}
        {info && <div style={{color:'#46e082', fontSize:12}}>{info}</div>}
        <button onClick={async ()=>{
          const payload = {
            email: form.email,
            password: form.password,
            first_name: form.first_name,
            last_name: form.last_name,
            nickname: form.nickname || undefined,
            country_code: form.country_code,
            region: form.region,
            postal_code: form.postal_code,
            age: form.age ? parseInt(form.age,10) : undefined,
            gender: (['M','F','N'].includes(form.gender) ? form.gender : 'N') as any
          }
          // rudimentary validation
          if (!payload.first_name || !payload.last_name || !payload.country_code || !payload.region || !payload.email || !payload.password) {
            setError('Vul alle verplichte velden in (land, provincie/regio, email, wachtwoord, naam).')
            return
          }
          const res = await signUpWithEmail(payload)
          if (res.error) setError(res.error)
          else if (res.needsConfirmation) setInfo('Check je e-mail om je account te bevestigen. Daarna kun je inloggen.')
  }} className="btn btn-primary btn-block">Account aanmaken</button>
      </div>
      <div style={{marginTop:10, fontSize:12}}>Al een account? <a onClick={onSwitch} style={{cursor:'pointer'}}>Log in</a></div>
    </div>
  )
}

// inputStyle no longer needed; using .input class from theme.css

// Country + region lists (compact, Dutch labels)
const COUNTRY_OPTIONS: Array<{ code: string; label: string; regions: string[] }> = [
  {
    code: 'NL', label: 'Nederland', regions: [
      'Drenthe','Flevoland','Friesland','Gelderland','Groningen','Limburg','Noord-Brabant','Noord-Holland','Overijssel','Utrecht','Zeeland','Zuid-Holland'
    ]
  },
  {
    code: 'BE', label: 'België', regions: [
      'Antwerpen','Limburg (BE)','Oost-Vlaanderen','Vlaams-Brabant','West-Vlaanderen',
      'Henegouwen','Luik','Luxemburg','Namen','Waals-Brabant','Brussel'
    ]
  },
  {
    code: 'US', label: 'Verenigde Staten', regions: [
      'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'
    ]
  },
  {
    code: 'ES', label: 'Spanje', regions: [
      'Andalucía','Aragón','Asturias','Islas Baleares','Canarias','Cantabria','Castilla-La Mancha','Castilla y León','Cataluña','Extremadura','Galicia','La Rioja','Comunidad de Madrid','Región de Murcia','Navarra','País Vasco','Comunidad Valenciana','Ceuta','Melilla'
    ]
  }
]
